import axios from "axios"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

export type ReleaseNoteImage = {
    id: number
    url: string
    filename: string
    alt_text: string | null
    sort_order: number
}

export type ReleaseNote = {
    id: number
    public_id: string
    release_date: string // ISO date-only "2026-07-15"
    title: string
    body_markdown: string
    status: "draft" | "published"
    published_at: string | null
    images: ReleaseNoteImage[]
}

export type ReleaseNoteInput = {
    title: string
    bodyMarkdown: string
    releaseDate: string // ISO date-only
}

// Newest-first page size — must match the API's DEFAULT_PAGE_SIZE so "has more"
// (a full page) is inferred correctly.
export const RELEASE_NOTES_PAGE_SIZE = 20

// Paginated, newest-first. The topbar What's New dot and the list page share this
// cache (same key): the dot only reads the first page's first note, while the list
// appends further pages via fetchNextPage. staleTime keeps the topbar button from
// refetching on every route change and stays well under the 1h presigned image-URL
// expiry so a cached page never holds dead image links.
export const useReleaseNotes = (includeDrafts = false) =>
    useInfiniteQuery({
        queryKey: ["release-notes", { includeDrafts }],
        queryFn: async ({ pageParam }) => {
            const params: Record<string, string> = { limit: String(RELEASE_NOTES_PAGE_SIZE), offset: String(pageParam) }
            if (includeDrafts) params.includeDrafts = "true"
            const response = await axios.get("release-notes", { params })
            return Array.isArray(response.data) ? (response.data as ReleaseNote[]) : []
        },
        initialPageParam: 0,
        // A short page means we've reached the end; otherwise the next offset is the running count.
        getNextPageParam: (lastPage, allPages) =>
            lastPage.length < RELEASE_NOTES_PAGE_SIZE ? undefined : allPages.length * RELEASE_NOTES_PAGE_SIZE,
        staleTime: 5 * 60 * 1000,
    })

// Single note for the shareable detail page, fetched by public_id so a deep link
// resolves even when the note lives on a not-yet-loaded list page. Drafts resolve
// only for super admins (the API 404s otherwise); retry is off so a genuine 404
// surfaces immediately as the not-found state.
export const useReleaseNote = (publicId: string | undefined) =>
    useQuery<ReleaseNote>({
        queryKey: ["release-note", publicId],
        queryFn: async () => {
            const response = await axios.get(`release-notes/${publicId}`)
            return response.data as ReleaseNote
        },
        enabled: !!publicId,
        staleTime: 5 * 60 * 1000,
        retry: false,
    })

const useInvalidatingMutation = <TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn,
        onSuccess: () => {
            // Refresh both the paginated list and any open single-note detail view.
            queryClient.invalidateQueries({ queryKey: ["release-notes"] })
            queryClient.invalidateQueries({ queryKey: ["release-note"] })
        },
    })
}

export const useCreateReleaseNote = () =>
    useInvalidatingMutation(async (input: ReleaseNoteInput) => {
        const response = await axios.post("release-notes", input)
        return response.data as ReleaseNote
    })

export const useUpdateReleaseNote = () =>
    useInvalidatingMutation(async ({ id, ...input }: Partial<ReleaseNoteInput> & { id: number }) => {
        const response = await axios.patch(`release-notes/${id}`, input)
        return response.data as ReleaseNote
    })

export const usePublishReleaseNote = () =>
    useInvalidatingMutation(async (id: number) => {
        const response = await axios.post(`release-notes/${id}/publish`)
        return response.data as ReleaseNote
    })

export const useUnpublishReleaseNote = () =>
    useInvalidatingMutation(async (id: number) => {
        const response = await axios.post(`release-notes/${id}/unpublish`)
        return response.data as ReleaseNote
    })

export const useDeleteReleaseNote = () =>
    useInvalidatingMutation(async (id: number) => {
        await axios.delete(`release-notes/${id}`)
    })

export const useDeleteReleaseNoteImage = () =>
    useInvalidatingMutation(async ({ noteId, imageId }: { noteId: number; imageId: number }) => {
        await axios.delete(`release-notes/${noteId}/images/${imageId}`)
    })

/**
 * Two-step authenticated upload (same flow as thread comment attachments):
 *  1. POST release-notes/:id/images/upload-url → presigned PUT + storage key
 *  2. PUT the file directly to S3 with raw `fetch` (NOT axios — the global
 *     interceptor would attach an Auth0 bearer header that invalidates the
 *     S3 signature)
 *  3. POST release-notes/:id/images to register the image row.
 */
export const uploadReleaseNoteImage = async (noteId: number, file: File, altText?: string): Promise<ReleaseNoteImage> => {
    const { data } = await axios.post<{ uploadUrl: string; storageKey: string; expiresIn: number }>(
        `release-notes/${noteId}/images/upload-url`,
        { filename: file.name, mimeType: file.type || "application/octet-stream", sizeBytes: file.size },
    )
    const res = await fetch(data.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
    })
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
    const created = await axios.post(`release-notes/${noteId}/images`, {
        storageKey: data.storageKey,
        filename: file.name,
        altText: altText || undefined,
    })
    return created.data as ReleaseNoteImage
}
